import React from 'react';
import moment from 'moment-timezone';
import { Button } from 'shared/components';
import { GqlImageBanner, ImageBannerPlatformType } from 'types/graphql';
import useUI from 'shared/hooks/use-ui';
import useHasMobileApp from 'src/utils/use-has-mobile-app';
import { daysOfWeek as daysOfWeekConstants } from 'shared/constants/time';
import { createScheduleTemplate, Day, getTimeDisplayString } from 'src/components/schedule-form/utils';
import {
  Container,
  Summary,
  Section,
  SectionHeader,
  SectionTitle,
  SummaryItems,
  SummaryItem,
  SummaryItemLabel,
  SummaryItemValue,
  EditButton,
  ButtonContainer,
  ImagePreview,
  ScheduleDateContainer,
  ScheduleDate,
  ScheduleTime,
  DayContainer,
  DayRow,
  DayName,
  Hours,
} from './confirm-step.styles';

type ConfirmStepProps = {
  banner: GqlImageBanner | undefined;
  setStepNumber: (newStep: number) => void;
  handleSave: () => Promise<void>;
  isSaving: boolean;
};

const imgixParams = {
  fit: 'crop',
  crop: 'edges',
};

export function ConfirmStep({ banner, setStepNumber, handleSave, isSaving }: ConfirmStepProps): JSX.Element {
  const hasMobileApp = useHasMobileApp();
  const UI = useUI();
  const dispensaryTimezone = UI.dispensary?.timezone || moment.tz.guess();

  const platforms = banner?.platforms ?? [];
  let webPlatform: typeof platforms[number] | undefined;
  let kioskPlatform: typeof platforms[number] | undefined;
  let iosPlatform: typeof platforms[number] | undefined;

  platforms.forEach((platform) => {
    if (platform?.type === ImageBannerPlatformType.web) {
      webPlatform = platform;
    } else if (platform?.type === ImageBannerPlatformType.kiosk) {
      kioskPlatform = platform;
    } else if (platform?.type === ImageBannerPlatformType.iosMobileApp) {
      iosPlatform = platform;
    }
  });

  const requiresDesktopImage = (webPlatform?.enabled ?? true) || (kioskPlatform?.enabled ?? true);
  const requiresMobileImage = (webPlatform?.enabled ?? true) || ((iosPlatform?.enabled ?? true) && hasMobileApp);

  // daysOfWeek is an array of the days of the week starting with index 1-6 of daysOfWeekConstants, with index 0 added at the end
  const daysOfWeek = daysOfWeekConstants.slice(1, 7).concat(daysOfWeekConstants[0]);
  const schedule = createScheduleTemplate(banner?.schedule?.timeRanges ?? [], false);

  return (
    <Container>
      <Summary>
        <Section>
          <SectionHeader>
            <SectionTitle>Configuration</SectionTitle>
            <EditButton onClick={() => setStepNumber(1)}>Edit</EditButton>
          </SectionHeader>

          <SummaryItems>
            <>
              {webPlatform && (
                <SummaryItem>
                  <SummaryItemLabel>Web:</SummaryItemLabel>
                  <SummaryItemValue>
                    {webPlatform.enabled ? `Enabled (${webPlatform.link ?? 'No link'})` : 'Disabled'}
                  </SummaryItemValue>
                </SummaryItem>
              )}

              {kioskPlatform && (
                <SummaryItem>
                  <SummaryItemLabel>Kiosk:</SummaryItemLabel>
                  <SummaryItemValue>
                    {kioskPlatform.enabled ? `Enabled (${kioskPlatform.link ?? 'No link'})` : 'Disabled'}
                  </SummaryItemValue>
                </SummaryItem>
              )}

              {iosPlatform && hasMobileApp && (
                <SummaryItem>
                  <SummaryItemLabel>Mobile App:</SummaryItemLabel>
                  <SummaryItemValue>
                    {iosPlatform.enabled ? `Enabled (${iosPlatform.link ?? 'No link'})` : 'Disabled'}
                  </SummaryItemValue>
                </SummaryItem>
              )}
            </>

            {requiresDesktopImage && (
              <SummaryItem>
                <SummaryItemLabel>Desktop Image:</SummaryItemLabel>
                <SummaryItemValue>
                  {banner?.image ? (
                    <ImagePreview
                      imgixParams={{ ...imgixParams, ar: '6:2', w: 300 }}
                      src={`${banner.image}`}
                      htmlAttributes={{ alt: banner.alt ?? '' }}
                    />
                  ) : (
                    'Not uploaded'
                  )}
                </SummaryItemValue>
              </SummaryItem>
            )}

            {requiresMobileImage && (
              <SummaryItem>
                <SummaryItemLabel>Mobile Image:</SummaryItemLabel>
                <SummaryItemValue>
                  {banner?.mobileImage ? (
                    <ImagePreview
                      imgixParams={{ ...imgixParams, ar: '2.22:1', w: 200 }}
                      src={`${banner.mobileImage}`}
                      htmlAttributes={{ alt: banner.alt ?? '' }}
                    />
                  ) : (
                    'Not uploaded'
                  )}
                </SummaryItemValue>
              </SummaryItem>
            )}

            <SummaryItem>
              <SummaryItemLabel>Alt Text:</SummaryItemLabel>
              <SummaryItemValue>{banner?.alt ?? 'None'}</SummaryItemValue>
            </SummaryItem>
          </SummaryItems>
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>Schedule</SectionTitle>
            <EditButton onClick={() => setStepNumber(2)}>Edit</EditButton>
          </SectionHeader>

          <SummaryItems>
            {banner?.schedule?.startDate ? (
              <>
                <SummaryItem>
                  <SummaryItemLabel>Start Date:</SummaryItemLabel>
                  <SummaryItemValue>
                    <ScheduleDateContainer>
                      <ScheduleDate>
                        {moment.tz(banner.schedule.startDate, dispensaryTimezone).format('MMM D, YYYY')}
                      </ScheduleDate>{' '}
                      |
                      <ScheduleTime>
                        {moment.tz(banner.schedule.startDate, dispensaryTimezone).format('h:mm A')}
                      </ScheduleTime>
                    </ScheduleDateContainer>
                  </SummaryItemValue>
                </SummaryItem>

                <SummaryItem>
                  <SummaryItemLabel>End Date:</SummaryItemLabel>
                  <ScheduleDateContainer>
                    {banner.schedule.endDate ? (
                      <>
                        <ScheduleDate>
                          {moment.tz(banner.schedule.endDate, dispensaryTimezone).format('MMM D, YYYY')}
                        </ScheduleDate>{' '}
                        |
                        <ScheduleTime>
                          {moment.tz(banner.schedule.endDate, dispensaryTimezone).format('h:mm A')}
                        </ScheduleTime>
                      </>
                    ) : (
                      <ScheduleDate>No End Date</ScheduleDate>
                    )}
                  </ScheduleDateContainer>
                </SummaryItem>

                <SummaryItem>
                  <SummaryItemLabel>Days and Hours:</SummaryItemLabel>
                  <SummaryItemValue>
                    <DayContainer>
                      {schedule.map((day: Day, idx: number) => (
                        <DayRow key={day.dayName}>
                          <DayName>{daysOfWeek[idx].short}</DayName> |
                          <Hours>{day.enabled ? getTimeDisplayString(day.startTime, day.endTime) : 'N/A'}</Hours>
                        </DayRow>
                      ))}
                    </DayContainer>
                  </SummaryItemValue>
                </SummaryItem>
              </>
            ) : (
              <SummaryItem>
                <SummaryItemLabel>Schedule:</SummaryItemLabel>
                <SummaryItemValue>No schedule set</SummaryItemValue>
              </SummaryItem>
            )}
          </SummaryItems>
        </Section>
      </Summary>

      <ButtonContainer>
        <Button onClick={() => void handleSave()} loading={isSaving}>
          Save Banner
        </Button>
      </ButtonContainer>
    </Container>
  );
}
